/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	UpdateVicinityThunk,
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	Snackbar,
	Stack,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { Close, EditOutlined } from "@mui/icons-material";
import CapitalizeFirstLetter from "../../../../../lib/CapitalizeFirstLetter";

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}
/* Stylings */

const PaperContainer = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const Container = styled("div")(({ theme }) => ({
	padding: "0rem 0rem 0rem 0rem",
	width: "47%",
	[theme.breakpoints.down("sm")]: {
		width: "100%",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const Text = styled(Typography)(({ theme }) => ({
	fontWeight: "400",
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const ContainerOne = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "flex-start",
	flexWrap: "wrap",
	width: "100%",
	gap: "2rem",
	[theme.breakpoints.down(theme.breakpoints.values.md + 100)]: {
		justifyContent: "space-between",
		gap: "0.5rem",
	},
}));

const SubContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "space-between",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "4rem",
}));

const CardsHolder = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
	flexWrap: "wrap",
	gap: "0.5rem",
}));

const VicinityTitleContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "0.25rem",
	margin: "0rem 0rem 1.25rem 0rem",
}));

const FieldsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "space-between",
	gap: "1rem",
	margin: "1rem 0rem 1rem 0rem",
	[theme.breakpoints.down("sm")]: {
		margin: "0.5rem 0rem 1rem 0rem",
		gap: "1.75rem",
	},
}));

const TitleDistanceContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "1rem",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
}));

const TitleField = styled(TextField)(({ theme }) => ({
	width: "75%",
	// margin: "0rem 0rem 0rem 0rem",
	[theme.breakpoints.down("sm")]: {
		width: "100%",
	},
}));

const DistanceField = styled(TextField)(({ theme }) => ({
	width: "20%",
	// margin: "0rem 0rem 1rem 0rem",
	[theme.breakpoints.down("sm")]: {
		width: "100%",
	},
}));

const Snack = styled(Snackbar)(({ theme }) => ({}));

const DispenseIcon = (icon = "vicinity/others") => {
	switch (icon) {
		case "vicinity/schools":
			return "/images/icons/singleproperty/vicinity/university_new.svg";
		case "vicinity/hospitals":
			return "/images/icons/singleproperty/vicinity/Hospital.svg";
		case "vicinity/malls":
			return "/images/icons/singleproperty/vicinity/malls_new.svg";
		case "vicinity/transport":
			return "/images/icons/singleproperty/vicinity/bus_stop.svg";
		// case "vicinity/it":
		//   return "/images/icons/singleproperty/vicinity/it_hub.svg";
		case "vicinity/others":
			return "/images/icons/singleproperty/vicinity/others.svg";
		default:
			return "/images/icons/singleproperty/vicinity/others.svg";
	}
};

const CalculateCategoryIcon = (category: string) => {
	switch (category) {
		case "schools":
			return "vicinity/schools";
		case "hospitals":
			return "vicinity/hospitals";
		case "malls":
			return "vicinity/malls";
		case "transport":
			return "vicinity/transport";
		// case "it":
		//   return "vicinity/it";
		case "others":
			return "vicinity/others";
		default:
			return "vicinity/others";
	}
};

const sortVicinityByDistance = (array: Array<property_vicinity_values>) => {
	let arr = [...array];

	return arr.sort((a: property_vicinity_values, b: property_vicinity_values) => (a.distance > b.distance ? 1 : -1));
};

interface property_vicinity_values {
	title: string;
	distance: number;
}

interface property_vicinity {
	category: string;
	values: Array<property_vicinity_values>;
}

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

function distance(lat1: number, lon1: number, lat2: number, lon2: number, unit: "K" | "M" | "N" = "K") {
	if (lat1 == lat2 && lon1 == lon2) {
		return 0;
	} else {
		const R = {
			K: 6371, // Radius of the Earth in kilometers
			M: 3958.8, // Radius of the Earth in miles
			N: 3440.1, // Radius of the Earth in nautical miles
		};

		const rad = (x: number) => (x * Math.PI) / 180;

		const dLat = rad(lat2 - lat1);
		const dLon = rad(lon2 - lon1);

		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		const distance = R[unit] * c;

		return distance;
	}
}

const Vicinity = ({
	session,
	property_vicinity,
	property_id,
	property_created_by,
	dispatch,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	assigned_to,
	vicinity_snackbar,
	location,
	trigger,
	updateTrigger,
}: {
	property_vicinity: Array<property_vicinity>;
	property_id: string | ObjectId;
	property_created_by: LocalPropertyInterface["created_by"];
	dispatch: Function;
	session: any;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	assigned_to: StateInterface["single_property_page"]["property"]["assigned_to"];
	vicinity_snackbar: boolean;
	location: [number, number];
	trigger: boolean;
	updateTrigger: (state: boolean) => void;
}) => {
	let propertyvicinity: Array<property_vicinity> = property_vicinity;
	const theme = useTheme();
	const [vicinities, setVicinities] = React.useState<Array<property_vicinity>>(propertyvicinity);
	const [isEditing, setIsEditing] = React.useState<boolean>(false);
	const [load, setLoad] = React.useState<boolean>(false);
	const [isAutoFillLoading, setAutoFillLoading] = React.useState(false);
	React.useEffect(() => {
		if (section_to_edit !== "property-vicinity") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	React.useEffect(() => {
		setVicinities(propertyvicinity);
	}, [propertyvicinity]);

	React.useEffect(() => {
		if (trigger) {
			setIsEditing(true);
			dispatch(setSectionToEditThunk("property-vicinity"));
			dispatch(setDataBeforeEditThunk(propertyvicinity));
			updateTrigger(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trigger]);

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router = React.useRef(useRouter()).current;

	const localLang = {
		snackValue:
			router.locale === "kn-IN"
				? "ನವೀಕರಣ ಯಶಸ್ವಿಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث"
					: "Vicinity updated successfully",
		vicinity: router.locale === "kn-IN" ? "ಪ್ರದೇಶ" : router.locale === "ar-AE" ? "جوار" : "Vicinity",
		schools: router.locale === "kn-IN" ? "ಶಾಲೆಗಳು" : router.locale === "ar-AE" ? "المدارس" : "Schools",
		hospitals: router.locale === "kn-IN" ? "ಆಸ್ಪತ್ರೆಗಳು" : router.locale === "ar-AE" ? "المستشفيات" : "Hospitals",
		malls: router.locale === "kn-IN" ? "ಮಾಲ್ಸ್" : router.locale === "ar-AE" ? "المراكز التجارية" : "Malls",
		others: router.locale === "kn-IN" ? "ಇತರರು" : router.locale === "ar-AE" ? "الآخرون" : "Others",
		km: router.locale === "kn-IN" ? "ಕಿಮೀ " : router.locale === "ar-AE" ? "كم " : " km",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		autofill: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "جلب تلقائيًا" : "Autofill",
	};

	if (loading) {
		return (
			<PaperContainer id="vicinity">
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Heading>
				</Contain>
				<ContainerOne>
					<Container>
						<Skeleton
							animation="wave"
							variant="text"
							width="3rem"
							height="4rem"
						/>
						<SubHeading>
							<Skeleton
								animation="wave"
								variant="text"
								width="10rem"
							/>
						</SubHeading>
						<SubContainer>
							<SubContainer sx={{ width: "80%" }}>
								<Text>
									<Skeleton
										animation="wave"
										variant="text"
										width="10rem"
										height="1rem"
									/>
								</Text>
							</SubContainer>
							<ContentContainer>
								<Text>-</Text>
								<Text>
									<Skeleton
										animation="wave"
										variant="text"
										width="3rem"
										height="1rem"
									/>
								</Text>
							</ContentContainer>
						</SubContainer>
					</Container>
					<Container>
						<Skeleton
							animation="wave"
							variant="text"
							width="3rem"
							height="4rem"
						/>
						<SubHeading>
							<Skeleton
								animation="wave"
								variant="text"
								width="10rem"
							/>
						</SubHeading>
						<SubContainer>
							<SubContainer sx={{ width: "80%" }}>
								<Text>
									<Skeleton
										animation="wave"
										variant="text"
										width="10rem"
										height="1rem"
									/>
								</Text>
							</SubContainer>
							<ContentContainer>
								<Text>-</Text>
								<Text>
									<Skeleton
										animation="wave"
										variant="text"
										width="3rem"
										height="1rem"
									/>
								</Text>
							</ContentContainer>
						</SubContainer>
					</Container>
				</ContainerOne>
			</PaperContainer>
		);
	}

	const vicinityCardContainer = document.getElementById("vicinity");

	const autoFillData = async () => {
		setAutoFillLoading(true);
		const cover = 5; // in km
		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("X-Goog-Api-Key", process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || "");
		headers.append("X-Goog-FieldMask", "*");
		const url = "https://places.googleapis.com/v1/places:searchNearby";
		const body = {
			includedTypes: [],
			locationRestriction: {
				circle: {
					center: {
						latitude: location[0],
						longitude: location[1],
					},
					radius: cover * 1000,
				},
			},
		};

		const fetchData = async (includedTypes: string[]) => {
			const response = await fetch(url, {
				method: "POST",
				headers,
				body: JSON.stringify({ ...body, includedTypes }),
			});
			return response.json();
		};

		const result = await Promise.all([
			fetchData(["school"]),
			fetchData(["hospital"]),
			fetchData(["shopping_mall"]),
			fetchData(["airport", "atm", "bus_station", "museum", "park", "restaurant", "zoo"]),
		]);

		const categories = ["school", "hospital", "shopping_mall", "others"];
		const data = categories.map((category, index) => {
			const item = result[index];
			if (!item.places) return [];
			const seenNames = new Set();
			const places = item.places.filter((res: any) => {
				if (category === "school") return res?.primaryType === "school";
				if (category === "hospital") return res?.primaryType === "hospital";
				if (category === "shopping_mall") return res?.primaryType === "shopping_mall";
				if (category === "others") return res?.primaryType !== "shopping_mall" || res?.primaryType !== "mall"; // all other types
				return true;
			});
			return places
				.map((res: any) => {
					const dist = distance(location[0], location[1], res.location.latitude, res.location.longitude);
					return {
						title: res.displayName.text,
						distance: +dist.toFixed(1) > 0 ? +dist.toFixed(1) : 0.1,
						userRatingCount: res.userRatingCount,
					};
				})
				.filter((place: { title: string }) => {
					if (seenNames.has(place.title)) {
						return false;
					} else {
						seenNames.add(place.title);
						return true;
					}
				})
				.sort((a: any, b: any) => b.userRatingCount - a.userRatingCount);
		});

		const formattedData = data.map((item) => {
			let items = [...item];

			const length = item.length;
			if (length < 4) {
				for (let i = 0; i < 4 - length; i++) {
					items.push({ title: "", distance: 0 });
				}
			} else if (length > 4) {
				items = items.slice(0, 4);
			}
			return items;
		});

		setVicinities((prev) => {
			let temp = prev.map((res, index) => ({ ...res, values: [...formattedData[index]] }));
			return temp;
		});

		const newVicinityValues: Array<property_vicinity> = vicinities.map((res, index) => ({
			...res,
			values: [...formattedData[index]],
		}));

		dispatch(setSinglePropertyVicinityThunk(newVicinityValues));
		setAutoFillLoading(false);
	};

	const submitForm = async () => {
		setLoad(true);
		await dispatch(
			UpdateVicinityThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				vicinity: vicinities,
				property_id: property_id,
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: property_created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (property_created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (property_created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
			}),
		);
		dispatch(setSectionToEditThunk(""));
		dispatch(setDataBeforeEditThunk({}));
		setIsEditing(false);
		vicinityCardContainer
			? vicinityCardContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: null;
		setLoad(false);
	};

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";
	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
					? (property_created_by.user_id as string)
					: property_created_by.user_id != ""
						? (property_created_by.user_id as UserInterface)._id
						: ""
			: ""
		: "";

	const is_user_creator: boolean =
		profile_context_user !== null
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	return (
		<PaperContainer id="vicinity">
			<Snack
				open={vicinity_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			{/* Icon */}

			<Contain>
				<Heading>{localLang.vicinity}</Heading>

				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("property-vicinity"));
									if (section_to_edit !== "property-vicinity") {
										switch (section_to_edit) {
											case "property-details":
												dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
												dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
												dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
												dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
												dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
												dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
												dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
												dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
												dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
												dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
												dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
												dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
												dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
												break;
											case "property-config":
												dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
												dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
												dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
												dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
												dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
												dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
												dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
												dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
												dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
												dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
												dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
												dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
												dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
												dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
												dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
												dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
												break;
											case "about-property":
												dispatch(setSinglePropertyAboutThunk(data_before_edit));
												break;
											case "property-amenities":
												dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
												break;
											case "property-developer":
												dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
												dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
												dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
												break;
											case "property-overview":
												dispatch(setSinglePropertyOverviewThunk(data_before_edit));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("property-vicinity"));
									}
									setIsEditing(true);
									dispatch(setDataBeforeEditThunk(propertyvicinity));
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									setIsEditing(false);
									dispatch(setSinglePropertyVicinityThunk(data_before_edit));
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			<ContainerOne>
				{!isEditing ? (
					/* User View */
					<React.Fragment>
						{propertyvicinity.map((item: property_vicinity, index: number) => {
							return (
								<Container key={index}>
									<VicinityTitleContainer sx={{ margin: "0rem 0rem 0rem 0rem" }}>
										<img
											loading="lazy"
											referrerPolicy="no-referrer"
											height={32}
											width={32}
											alt="icon"
											src={DispenseIcon(CalculateCategoryIcon(item.category))}
										/>

										<SubHeading>{CapitalizeFirstLetter(item.category)}</SubHeading>
									</VicinityTitleContainer>

									<FieldsContainer
										sx={{
											margin: "0.5rem 0rem 0rem 0rem",
											gap: "0.5rem",
											[theme.breakpoints.down("sm")]: {
												gap: "0.5rem",
											},
										}}
									>
										{sortVicinityByDistance(item.values).map(
											(obj: property_vicinity_values, k: number) =>
												obj.title !== " " &&
												obj.distance !== 0 && (
													<SubContainer key={k}>
														<SubContainer sx={{ width: "80%" }}>
															<Text>{obj.title}</Text>
														</SubContainer>
														<ContentContainer>
															<Text>-</Text>
															<Text>{obj.distance ? obj.distance.toFixed(1) + localLang.km : undefined}</Text>
														</ContentContainer>
													</SubContainer>
												),
										)}
									</FieldsContainer>
								</Container>
							);
						})}
					</React.Fragment>
				) : (
					/* Edit View */
					<div>
						<CardsHolder>
							{propertyvicinity.map((item: property_vicinity, index: number) => {
								return (
									<Container key={index}>
										<VicinityTitleContainer>
											<img
												loading="lazy"
												referrerPolicy="no-referrer"
												height={28}
												width={28}
												alt="icon"
												src={DispenseIcon(CalculateCategoryIcon(item.category))}
											/>

											<SubHeading>{CapitalizeFirstLetter(item.category)}</SubHeading>
										</VicinityTitleContainer>

										<FieldsContainer>
											{item.values.map((obj: property_vicinity_values, k: number) => {
												return (
													<TitleDistanceContainer key={k}>
														<TitleField
															id={"spp_vicinity_edit_textfield"}
															label="Title"
															variant="outlined"
															size="small"
															value={obj.title}
															autoComplete="off"
															sx={{
																width: "100%",
																"& .MuiInputBase-input": {
																	fontSize: "0.825rem",
																	[theme.breakpoints.up("sm")]: {
																		fontSize: "0.875rem",
																	},
																	[theme.breakpoints.up("md1190")]: {
																		fontSize: "1rem",
																	},
																},
															}}
															onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																if (section_to_edit !== "property-vicinity") {
																	dispatch(setSectionToEditThunk("property-vicinity"));
																}

																let newValue = [...propertyvicinity];

																const oldValue = propertyvicinity[index].values[k];
																const newValue1 = {
																	...oldValue,
																	title: e.target.value,
																};
																const valuesClone = [...propertyvicinity[index].values];

																valuesClone[k] = newValue1;

																const newValue2 = {
																	...propertyvicinity[index],
																	values: valuesClone,
																};
																newValue[index] = newValue2;
																dispatch(setSinglePropertyVicinityThunk(newValue));
															}}
														/>
														<DistanceField
															id={"spp_vicinity_distance_field_edit_view"}
															label="KM"
															variant="outlined"
															size="small"
															type="number"
															autoComplete="off"
															sx={{
																width: "100%",
																"& .MuiInputBase-input": {
																	fontSize: "0.825rem",
																	[theme.breakpoints.up("sm")]: {
																		fontSize: "0.875rem",
																	},
																	[theme.breakpoints.up("md1190")]: {
																		fontSize: "1rem",
																	},
																},
															}}
															slotProps={{
																htmlInput: {
																	min: 0,
																	inputMode: "numeric",
																	pattern: "[0-9]*",
																},
															}}
															onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
																if (event.key === "Enter" || event.key === "Next") {
																	(event.target as HTMLInputElement).blur();
																}
															}}
															value={obj.distance === 0 ? "" : obj.distance}
															onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																if (section_to_edit !== "property-vicinity") {
																	dispatch(setSectionToEditThunk("property-vicinity"));
																}
																let newValue = [...propertyvicinity];

																const oldValue = propertyvicinity[index].values[k];
																const newValue1 = {
																	...oldValue,
																	distance: parseFloat(e.target.value),
																};

																const valuesClone = [...propertyvicinity[index].values];

																valuesClone[k] = newValue1;

																const newValue2 = {
																	...propertyvicinity[index],
																	values: valuesClone,
																};

																newValue[index] = newValue2;

																dispatch(setSinglePropertyVicinityThunk(newValue));
															}}
														/>
													</TitleDistanceContainer>
												);
											})}
										</FieldsContainer>
									</Container>
								);
							})}
						</CardsHolder>

						{/* Update Button */}
						{load ? (
							<CircularProgress
								// size={24}
								sx={{
									marginTop: "1rem",
								}}
							/>
						) : (
							<Stack
								spacing={1}
								direction={"row"}
							>
								<BeegruButton
									id="spp_vicinity_update_button"
									flavor="primary"
									onClick={submitForm}
									variant="contained"
									type="button"
									name="update vicinity"
								>
									{localLang.update}
								</BeegruButton>
								<BeegruButton
									id="spp_vicinity_autofill_button"
									flavor="secondary"
									onClick={isAutoFillLoading ? undefined : autoFillData}
									variant="outlined"
									type="button"
									name="update vicinity"
								>
									{isAutoFillLoading ? <CircularProgress size={20} /> : localLang.autofill}
								</BeegruButton>
							</Stack>
						)}
					</div>
				)}
			</ContainerOne>
		</PaperContainer>
	);
};
export default Vicinity;
